import { obtenerMensajeError } from "utils/apiErrors";
import { useState, useEffect } from "react";
import PropTypes from "prop-types";

import {
  Container,
  Typography,
  Tabs,
  Button,
  Tab,
  Box,
  Backdrop,
  CircularProgress,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import EditIcon from "@mui/icons-material/Edit";

import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";
import FileDialog from "components/FileDialog";

import dayjs from "dayjs";
import "dayjs/locale/es";
import weekday from "dayjs/plugin/weekday";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
dayjs.extend(weekday);
dayjs.extend(isSameOrBefore);

import ContainerQuorum from "./containerQuorum";
import ContainerReparos from "./containerReparos";
import ContainerConciliacion from "./containerConciliacion";
import ContainerObjeciones from "./containerObjeciones";
import ContainerNegociacion from "./containerNegociacion";
import ContainerResultado from "./containerResultado";

import useUserRoles from "hooks/useUserRoles";

import EditarAudiencia from "./editarAudiencia";
import ReprogramaModal from "./reprogramarAudiencia";
import ProrrogaModal from "./prorrogarProceso";
import AperturaModal from "./aperturarProceso";
import RevocacionModal from "./revocarProceso";
import TerminacionModal from "./terminarProceso";
import ConvocarAudiencia from "./convocarAudiencia";

import DatosBasicosApi from "api/datosbasicosApi";
import PDFApi from "api/pdfApi";
import BucketApi from "api/bucketApi";
import ProcesosApi from "api/procesosApi";

const ContainerAudiencias = ({ proceso, onProcesoChange }) => {
  const { user, roles } = useUserRoles();
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState("");
  const [tabIndex, setTabIndex] = useState(0);
  const [tabIndexEtapas, setTabIndexEtapas] = useState(0);
  const [audiencias, setAudiencias] = useState([]);
  const [audienciaActual, setAudienciaActual] = useState(null);
  const [penultimaAudiencia, setPenultimaAudiencia] = useState(null);
  const [etapaNegociacion, setEtapaNegociacion] = useState(null);
  const [etapaResultado, setEtapaResultado] = useState(null);
  const [fechaNuevaAudiencia, setFechaNuevaAudiencia] = useState("");
  const [horaNuevaAudiencia, setHoraNuevaAudiencia] = useState("");
  const [reloadFlag, setReloadFlag] = useState(false);
  const [audienciaParaEditar, setAudienciaParaEditar] = useState(null);
  const [modalEditar, setModalEditar] = useState(false);
  const [modalReprogramarOpen, setModalReprogramarOpen] = useState(false);
  const [modalProrrogarOpen, setModalProrrogarOpen] = useState(false);
  const [modalAperturarOpen, setModalAperturarOpen] = useState(false);
  const [modalRevocarOpen, setModalRevocarOpen] = useState(false);
  const [modalTerminarOpen, setModalTerminarOpen] = useState(false);
  const [modalConvocarOpen, setModalConvocarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [suspendido, setSuspendido] = useState(false);
  //const [estadoProceso, setEstadoProceso] = useState("");
  const [cantDiasGestion, setCantDiasGestion] = useState(0);
  const [diasFestivos, setDiasFestivos] = useState([]);
  const [operationLoading, setOperationLoading] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [currentOperation, setCurrentOperation] = useState("");

  const isProcessing = () => {
    return loading || operationLoading || pdfGenerating;
  };
  const getLoadingMessage = () => {
    if (pdfGenerating) return "Generando PDF...";
    if (currentOperation) return currentOperation;
    if (loading) return "Cargando datos...";
    return "Procesando...";
  };

  useEffect(() => {
    const cacheKey = "dias_festivos_cache";
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      setDiasFestivos(JSON.parse(cached));
      return;
    }
    const fetchDias = async () => {
      const resp = await DatosBasicosApi.fetchDiasFestivos();
      const data = Array.isArray(resp.data) ? resp.data : [];
      localStorage.setItem(cacheKey, JSON.stringify(data));
      setDiasFestivos(data);
    };
    fetchDias();
  }, []);

  const fetchDatosIniciales = async () => {
    setLoading(true);
    try {
      const procesoResponse = await ProcesosApi.fetchProcesoById(proceso.id);
      if (procesoResponse.data) {
        setCantDiasGestion(procesoResponse.data.dias_habiles_restantes);
        if (procesoResponse.data.fecha_suspension) {
          setSuspendido(true);
        } else {
          setSuspendido(false);
        }
      }
      const respAudiencias = await ProcesosApi.obtenerAudienciasProcesoSoft(proceso.id);
      const audiencias = respAudiencias.data || [];
      setAudiencias(Array.isArray(respAudiencias.data) ? respAudiencias.data : []);
      if (audiencias.length >= 2) {
        const Audiencia = audiencias[audiencias.length - 2];
        setPenultimaAudiencia(Audiencia);
      }
    } catch (error) {
      console.error("Error al obtener los datos iniciales:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (proceso) {
      fetchDatosIniciales();
    }
  }, [user, proceso, reloadFlag]);

  useEffect(() => {
    if (audiencias.length > 0 && tabIndex >= 0 && tabIndex < audiencias.length) {
      setAudienciaActual(audiencias[tabIndex]);
      setEtapaNegociacion(audiencias[tabIndex].etapas?.find((etapa) => etapa.orden === 5) || null);
      setEtapaResultado(audiencias[tabIndex].etapas?.find((etapa) => etapa.orden === 6) || null);
    }
  }, [tabIndex, audiencias]);

  const isNonWorkingDay = (date) => {
    return (
      dayjs(date).day() === 0 ||
      dayjs(date).day() === 6 ||
      diasFestivos?.includes(dayjs(date).format("YYYY-MM-DD"))
    );
  };

  const calcularFechaLimite = (diasHabilesRestantes) => {
    let fechaActual = dayjs();
    let diasContados = 0;
    while (diasContados < diasHabilesRestantes) {
      fechaActual = fechaActual.add(1, "day");
      if (!isNonWorkingDay(fechaActual)) {
        diasContados++;
      }
    }
    return fechaActual;
  };

  const handleChangeTab = (event, newValue) => {
    if (isProcessing()) return;
    setTabIndex(newValue);
    setTabIndexEtapas(0);
  };

  const handleChangeEtapas = (event, newIndex) => {
    if (isProcessing()) return;
    setTabIndexEtapas(newIndex);
  };

  const handleAudienciaChange = (updatedAudiencia) => {
    // Actualizar la audiencia actual en el estado local inmediatamente
    setAudiencias((prev) =>
      prev.map((aud) => (aud.id === updatedAudiencia.id ? updatedAudiencia : aud))
    );
    // También actualizar audienciaActual si es la misma
    if (audienciaActual?.id === updatedAudiencia.id) {
      setAudienciaActual(updatedAudiencia);
    }
    // Luego hacer el fetch para sincronizar con la base de datos
    fetchDatosIniciales();
  };

  const handleActaAudiencia = async (estadoAudiencia, consAudiencia) => {
    if (isProcessing()) return;
    let FILE_NAME = ``;
    let FILE_TYPE = ``;
    if (estadoAudiencia === "SUSPENDIDA") {
      FILE_NAME = `audiencia_${consAudiencia}.pdf`;
      FILE_TYPE = `audiencias`;
    } else if (estadoAudiencia === "CERRADA") {
      if (audienciaActual.resultado == "proceso_en_acuerdo") {
        FILE_NAME = `pdf_reforma_acuerdo.pdf`;
        FILE_TYPE = `proceso`;
      } else if (audienciaActual.resultado == "proceso_incumplido") {
        FILE_NAME = `pdf_incumplimiento.pdf`;
        FILE_TYPE = `proceso`;
      } else {
        FILE_NAME = `pdf_cierre.pdf`;
        FILE_TYPE = `proceso`;
      }
    }
    try {
      setCurrentOperation("Cargando PDF...");
      setOperationLoading(true);
      const response = await BucketApi.checkFileExists(FILE_NAME, FILE_TYPE, {
        proceso_id: proceso.identificador,
      });
      const exists = response.data.exists;
      if (exists) {
        const urlResponse = await BucketApi.getFileUrlWithCacheBuster(FILE_NAME, FILE_TYPE, {
          proceso_id: proceso.identificador,
        });
        const newUrl = urlResponse.data.file_url;
        if (newUrl) {
          setCurrentPdfUrl(newUrl);
          setShowPdfViewer(true);
        }
      } else {
        alert("El PDF no existe.");
      }
    } catch (error) {
      console.error("Error obteniendo URL del PDF:", error);
    } finally {
      setOperationLoading(false);
      setCurrentOperation("");
    }
  };
  const abrirModalEditarAudiencia = (audiencia) => {
    setAudienciaParaEditar(audiencia);
    setModalEditar(true);
  };

  const handleReprogramarAudiencia = () => {
    if (isProcessing()) return;
    const etapas = audienciaActual?.etapas || [];
    const etapaQuorum = etapas.find((etapa) => etapa.orden === 1);
    if (etapaQuorum.estado === "CUMPLIDA") {
      setModalReprogramarOpen(true);
    } else {
      alert("No se puede avanzar: Quórum no completado.");
      return;
    }
  };

  const handleProrrogarProceso = () => {
    if (isProcessing()) return;
    const etapas = audienciaActual?.etapas || [];
    const etapaQuorum = etapas.find((etapa) => etapa.orden === 1);
    if (etapaQuorum.estado === "CUMPLIDA") {
      setModalProrrogarOpen(true);
    } else {
      alert("No se puede avanzar: Quórum no completado.");
      return;
    }
  };

  const handleAperturarProceso = () => {
    if (isProcessing()) return;
    const etapas = audienciaActual?.etapas || [];
    const etapaQuorum = etapas.find((etapa) => etapa.orden === 1);
    if (etapaQuorum.estado === "CUMPLIDA") {
      setModalAperturarOpen(true);
    } else {
      alert("No se puede avanzar: Quórum no completado.");
      return;
    }
  };

  const handleRevocarProceso = () => {
    if (isProcessing()) return;
    const etapas = audienciaActual?.etapas || [];
    const etapaQuorum = etapas.find((etapa) => etapa.orden === 1);
    if (etapaQuorum.estado === "CUMPLIDA") {
      setModalRevocarOpen(true);
    } else {
      alert("No se puede avanzar: Quórum no completado.");
      return;
    }
  };

  const handleTerminarProceso = () => {
    if (isProcessing()) return;
    const etapas = audienciaActual?.etapas || [];
    const etapaQuorum = etapas.find((etapa) => etapa.orden === 1);
    if (etapaQuorum.estado === "CUMPLIDA") {
      setModalTerminarOpen(true);
    } else {
      alert("No se puede avanzar: Quórum no completado.");
      return;
    }
  };

  const generarPdfAudiencia = async (nombreActa, consecutivo) => {
    try {
      setPdfGenerating(true);
      setCurrentOperation("Generando PDF de audiencia...");
      const FILE_NAME = `audiencia_${consecutivo}.pdf`;
      const ANEXO_TYPE = `AUDIENCIA_${consecutivo}`;
      const anexoResponse = await ProcesosApi.obtenerAnexoProcesoByTipo(proceso.id, {
        tipo_anexo: ANEXO_TYPE,
      });
      const anexo = anexoResponse.data;
      if (anexo != undefined && !anexoResponse.data.error) {
        await ProcesosApi.eliminarAnexoProceso(proceso.id, anexo.id);
      }
      setCurrentOperation("Generando documento PDF...");
      const pdfResponse = await PDFApi.generateDocument(nombreActa, audienciaActual.id, {
        responseType: "blob",
      });
      setCurrentOperation("Subiendo archivo...");
      const pdfBlob = new Blob([pdfResponse.data], { type: "application/pdf" });
      const formData = new FormData();
      formData.append("file", pdfBlob, FILE_NAME);
      formData.append("file_type", "audiencias");
      formData.append("proceso_id", proceso.identificador);
      formData.append("registrar_anexo", "true");
      formData.append("tipo_anexo", ANEXO_TYPE);
      formData.append("nombre_anexo", `Audiencia No. ${consecutivo}`);
      await BucketApi.uploadFile(formData);
      const url = URL.createObjectURL(pdfBlob);
      setCurrentPdfUrl(url);
      setShowPdfViewer(true);
    } catch (error) {
      console.error("Error en el proceso de generación de PDF:", error);
    } finally {
      setPdfGenerating(false);
      setCurrentOperation("");
    }
  };

  const generarPdfProceso = async (nombreArchivo) => {
    try {
      setPdfGenerating(true);
      setCurrentOperation("Generando PDF de proceso...");
      let FILE_NAME = "pdf_cierre.pdf";
      let ANEXO_TYPE = "PDF_CIERRE";
      let NOMBRE_ANEXO = "";
      if (nombreArchivo == "cierreAcuerdo") {
        NOMBRE_ANEXO = "Cierre Acuerdo";
      } else if (nombreArchivo == "cierreFracaso") {
        NOMBRE_ANEXO = "Cierre Fracaso";
      } else if (nombreArchivo == "cierreAcuerdoBilateral") {
        NOMBRE_ANEXO = "Cierre Acuerdo Bilateral";
      } else if (nombreArchivo == "actaTerminacionProceso") {
        NOMBRE_ANEXO = "Acta Terminación";
      }
      const anexoResponse = await ProcesosApi.obtenerAnexoProcesoByTipo(proceso.id, {
        tipo_anexo: ANEXO_TYPE,
      });
      const anexo = anexoResponse.data;
      if (anexo != undefined && !anexoResponse.data.error) {
        await ProcesosApi.eliminarAnexoProceso(proceso.id, anexo.id);
      }
      setCurrentOperation("Generando documento PDF...");
      const pdfResponse = await PDFApi.generateDocument(nombreArchivo, audienciaActual.id, {
        responseType: "blob",
      });
      setCurrentOperation("Subiendo archivo...");
      const pdfBlob = new Blob([pdfResponse.data], { type: "application/pdf" });
      const formData = new FormData();
      formData.append("file", pdfBlob, FILE_NAME);
      formData.append("file_type", "proceso");
      formData.append("proceso_id", proceso.identificador);
      formData.append("registrar_anexo", "true");
      formData.append("tipo_anexo", ANEXO_TYPE);
      formData.append("nombre_anexo", NOMBRE_ANEXO);
      await BucketApi.uploadFile(formData);
      const url = URL.createObjectURL(pdfBlob);
      setCurrentPdfUrl(url);
      setShowPdfViewer(true);
    } catch (error) {
      console.error("Error en el proceso de generación de PDF:", error);
    } finally {
      setPdfGenerating(false);
      setCurrentOperation("");
    }
  };

  const pdfConfig = {
    determinarTipo: (etapas) => {
      //console.log("etapas: ", etapas);
      // Mapeo de condiciones a tipos de PDF, ORDENADAS por prioridad ASCENDENTE (etapa 2 primero, etapa 6 último)
      const conditions = [
        { order: 1, status: "CUMPLIDA", type: "QUORUM_CUMPLIDA" }, // Primera prioridad
        { order: 2, status: "PENDIENTE", type: "REPAROS_PENDIENTE" },
        { order: 2, status: "CUMPLIDA", type: "REPAROS" },
        { order: 3, status: "PENDIENTE", type: "CONCILIACION_PENDIENTE" },
        { order: 3, status: "CUMPLIDA", type: "CONCILIACION" },
        { order: 4, status: "CERRADA", type: "OBJECIONES_CERRADA" },
        { order: 4, status: "PENDIENTE", type: "OBJECIONES_PENDIENTE" },
        { order: 4, status: "CUMPLIDA", type: "OBJECIONES" },
        { order: 5, status: "PENDIENTE", type: "NEGOCIACION_PENDIENTE" },
        { order: 5, status: "CUMPLIDA", type: "NEGOCIACION" },
        { order: 6, status: "PENDIENTE", type: "RESULTADO_PENDIENTE" },
        { order: 6, status: "CUMPLIDA", type: "RESULTADO" }, // Última prioridad
      ].sort((a, b) => b.order - a.order); // Orden ASCENDENTE (2 → 6)
      for (const condition of conditions) {
        if (etapas?.some((e) => e.orden === condition.order && e.estado === condition.status)) {
          //alert("condition: " + JSON.stringify(condition));
          return condition.type;
        }
      }
      return null;
    },
    mensajes: {
      QUORUM_CUMPLIDA:
        "La Audiencia se suspende en la etapa de Quorum, se genera Acta de Reprogramación.",
      REPAROS_PENDIENTE:
        "La Audiencia se suspende en la etapa de Control de Legalidad CON Reparos en estado Presentado.",
      REPAROS:
        "La Audiencia se suspende en la etapa de Control de Legalidad SIN Reparos en estado Presentado.",
      CONCILIACION_PENDIENTE:
        "La Audiencia se suspende en la etapa de Conciliación de Acreencias en estado PENDIENTE.",
      CONCILIACION:
        "La Audiencia se suspende en la etapa de Conciliación de Acreencias en estado CUMPLIDA.",
      OBJECIONES_CERRADA:
        "La Audiencia se suspende en la etapa de Objecciones (CERRADA), con Objeciones en firme. Se registrará una Prórroga de 10 días al Proceso.",
      OBJECIONES_PENDIENTE:
        "La Audiencia se suspende en la etapa de Objecciones en estado PENDIENTE.",
      OBJECIONES:
        "La Audiencia se suspende en la etapa de Objecciones en estado CUMPLIDA, sin objeciones en estado Presentada.",
      NEGOCIACION_PENDIENTE:
        "La Audiencia se suspende en la etapa de Negociación en estado PENDIENTE.",
      NEGOCIACION: "La Audiencia se suspende en la etapa de Negociación en estado CUMPLIDA.",
      RESULTADO_PENDIENTE: "La Audiencia se suspende en la etapa de Resultado en estado PENDIENTE.",
      RESULTADO: "La Audiencia se suspende con la etapa de Resultado en estado CUMPLIDA.",
    },
  };

  const generarPdfPorTipo = async (tipo, consecutivo) => {
    //alert("tipo: " + JSON.stringify(tipo));
    let pdfAGenerar = "";
    switch (tipo) {
      case "QUORUM_CUMPLIDA":
        await generarPdfAudiencia("suspensionReprogramacion", consecutivo);
        break;
      case "REPAROS_PENDIENTE":
        await generarPdfAudiencia("suspensionReparos", consecutivo);
        break;
      case "REPAROS":
        await generarPdfAudiencia("suspensionReparos", consecutivo);
        break;
      case "CONCILIACION_PENDIENTE":
      case "CONCILIACION":
        await generarPdfAudiencia("suspensionConciliacion", consecutivo);
        break;
      case "OBJECIONES_CERRADA":
        await generarPdfAudiencia("suspensionObjecionesFirmes", consecutivo);
        break;
      case "OBJECIONES_PENDIENTE":
        await generarPdfAudiencia("suspensionObjecionesPlanteadas", consecutivo);
        break;
      case "OBJECIONES":
        await generarPdfAudiencia("suspensionObjecionesPlanteadas", consecutivo);
        break;
      case "NEGOCIACION_PENDIENTE":
        await generarPdfAudiencia("suspensionNegociacionMejorada", consecutivo);
        //await generarPdfAudiencia("suspensionNegociacion", consecutivo);
        break;
      case "NEGOCIACION":
        await generarPdfAudiencia("suspensionNegociacion", consecutivo);
        //await generarPdfAudiencia("suspensionNegociacionMejorada", consecutivo);
        break;
      case "RESULTADO_PENDIENTE":
      case "RESULTADO":
        if (etapaNegociacion?.resultado.resultado === "ACUERDO DE PAGO") {
          pdfAGenerar = "cierreAcuerdo";
        } else if (etapaNegociacion?.resultado.resultado.includes("FRACASO")) {
          const tieneAcuerdoBilateral =
            etapaResultado.resultado?.info_acreedores &&
            Object.values(etapaResultado.resultado.info_acreedores).some(
              (a) => a.acuerdo_bilateral === true
            );
          if (tieneAcuerdoBilateral) {
            pdfAGenerar = "cierreAcuerdoBilateral";
          } else {
            pdfAGenerar = "cierreFracaso";
          }
        }
        await generarPdfAudiencia(pdfAGenerar, consecutivo);
        break;
      default:
        break;
    }
  };

  const manejarProrrogaObjecionesCerradas = async ({ proceso, audienciaId, onProcesoChange }) => {
    const nuevaProrroga = {
      orden: proceso.prorrogas ? Object.keys(proceso.prorrogas).length + 1 : 1,
      cantidad_dias: 10, // 5 dias para el OBJETANTE y 5 dias para el DEUDOR
      fecha_creacion: new Date().toISOString(),
      observacion: "Prórroga automática por Objeciones en Firmes.",
      id_audiencia: audienciaId,
    };
    const prorrogasActualizadas = proceso.prorrogas
      ? [...proceso.prorrogas, nuevaProrroga]
      : [nuevaProrroga];
    await ProcesosApi.actualizarProceso(proceso.id, { prorrogas: prorrogasActualizadas });
    if (onProcesoChange) {
      onProcesoChange({
        ...proceso,
        prorrogas: prorrogasActualizadas,
      });
    }
  };

  const handleSuspenderAudiencia = async () => {
    if (isProcessing()) return;
    const etapaQuorum = audienciaActual?.etapas.find((etapa) => etapa.orden === 1);
    if (etapaQuorum.estado !== "CUMPLIDA") {
      alert("No se puede avanzar: Quórum no completado.");
      return;
    }
    if (!fechaNuevaAudiencia || !horaNuevaAudiencia) {
      alert("Por favor, ingrese la fecha y hora de la nueva audiencia.");
      return;
    }
    const fechaLimite = calcularFechaLimite(cantDiasGestion);
    const fechaNuevaAudienciaObj = dayjs(fechaNuevaAudiencia);
    if (fechaNuevaAudienciaObj.isAfter(fechaLimite)) {
      alert(
        `La nueva fecha de audiencia no puede superar el límite de ${cantDiasGestion} días hábiles de gestión (${fechaLimite.format(
          "DD/MM/YYYY"
        )}).`
      );
      return;
    }
    if (isNonWorkingDay(fechaNuevaAudienciaObj)) {
      alert(
        "La nueva fecha de audiencia no puede ser en un día no hábil (sábado, domingo o festivo)."
      );
      return;
    }
    const tipoPDFAGenerar = pdfConfig.determinarTipo(audienciaActual?.etapas);
    const mensajeConfirm = tipoPDFAGenerar ? pdfConfig.mensajes[tipoPDFAGenerar] : "";
    const confirmacion = window.confirm(
      `¿Está seguro(a) de que desea Suspender la Audiencia? ${mensajeConfirm}`
    );
    if (!confirmacion) return;
    const datosSuspension = {
      operacion: "SUSPENDER_AUDIENCIA",
      id_audiencia: audienciaActual?.id,
      nueva_fecha: fechaNuevaAudiencia,
      nueva_hora: horaNuevaAudiencia,
    };

    try {
      setCurrentOperation("Suspendiendo audiencia...");
      setOperationLoading(true);
      await ProcesosApi.operarAudiencia(datosSuspension);
      if (tipoPDFAGenerar === "OBJECIONES_CERRADA") {
        let idAudiencia = audienciaActual?.id;
        await manejarProrrogaObjecionesCerradas({ proceso, idAudiencia, onProcesoChange });
      }
      await generarPdfPorTipo(tipoPDFAGenerar, audienciaActual?.consecutivo);
      setFechaNuevaAudiencia("");
      setHoraNuevaAudiencia("");
      setReloadFlag((prev) => !prev);
      if (onProcesoChange) {
        await onProcesoChange();
      }
      alert("La Audiencia ha sido suspendida correctamente.");
    } catch (error) {
      console.error("Error en al Operar la Audiencia:", error);
      alert(obtenerMensajeError(error, "Ocurrió un error al Operar la Audiencia."));
    } finally {
      setOperationLoading(false);
      setCurrentOperation("");
    }
  };

  const handleAbrirAudiencia = async (audienciaId) => {
    if (isProcessing()) return;
    const confirmacion = window.confirm(`¿Está seguro(a) de que desea abrir la Audiencia?`);
    if (!confirmacion) return;
    const datosApertura = {
      operacion: "ABRIR_AUDIENCIA",
      id_audiencia: audienciaId,
    };
    try {
      setCurrentOperation("Abriendo audiencia...");
      setOperationLoading(true);
      await ProcesosApi.operarAudiencia(datosApertura);
      setFechaNuevaAudiencia("");
      setHoraNuevaAudiencia("");
      setReloadFlag((prev) => !prev);
      if (onProcesoChange) {
        await onProcesoChange();
      }
      alert("La Audiencia ha sido abierta correctamente.");
    } catch (error) {
      console.error("Error en al Abrir la Audiencia:", error);
      alert(obtenerMensajeError(error, "Ocurrió un error al Abrir la Audiencia."));
    } finally {
      setOperationLoading(false);
      setCurrentOperation("");
    }
  };

  const handleCerrarAudiencia = async () => {
    if (isProcessing()) return;
    const tipoPDFAGenerar = pdfConfig.determinarTipo(audienciaActual?.etapas);
    const mensajeConfirm = tipoPDFAGenerar ? pdfConfig.mensajes[tipoPDFAGenerar] : "";
    const confirmacion = window.confirm(
      `¿Está seguro(a) de que desea cerrar la Audiencia con las modificaciones realizadas? ${mensajeConfirm}`
    );
    if (!confirmacion) return;
    const datosCierre = {
      operacion: "CERRAR_AUDIENCIA", //En realidad se SUSPENDE ya que es la penultima Audiencia
      id_audiencia: audienciaActual?.id,
    };
    try {
      setCurrentOperation("Cerrando audiencia...");
      setOperationLoading(true);
      await ProcesosApi.operarAudiencia(datosCierre);
      if (tipoPDFAGenerar === "OBJECIONES_CERRADA") {
        let idAudiencia = audienciaActual?.id;
        await manejarProrrogaObjecionesCerradas({ proceso, idAudiencia, onProcesoChange });
      }
      await generarPdfPorTipo(tipoPDFAGenerar, audienciaActual?.consecutivo);
      setFechaNuevaAudiencia("");
      setHoraNuevaAudiencia("");
      setReloadFlag((prev) => !prev);
      if (onProcesoChange) {
        await onProcesoChange();
      }
      alert("La Audiencia ha sido cerrada correctamente.");
    } catch (error) {
      console.error("Error en al Cerrar la Audiencia:", error);
      alert(obtenerMensajeError(error, "Ocurrió un error al Cerrar la Audiencia."));
    } finally {
      setOperationLoading(false);
      setCurrentOperation("");
    }
  };

  const handleSuspenderActivarTerminos = async () => {
    if (isProcessing()) return;
    const etapas = audienciaActual?.etapas || [];
    const etapaQuorum = etapas.find((etapa) => etapa.orden === 1);
    if (etapaQuorum.estado !== "CUMPLIDA") {
      alert("No se puede avanzar: Quórum no completado.");
      return;
    }
    let DescOperacion = "SUSPENDER_TERMINOS";
    let MensOperacion = "Suspender";
    if (suspendido) {
      DescOperacion = "ACTIVAR_TERMINOS";
      MensOperacion = "Activar";
    }
    const confirmacion = window.confirm(
      `¿Está seguro(a) de que desea ${MensOperacion} los Términos del Proceso?`
    );
    if (!confirmacion) return;
    const datosTerminos = {
      operacion: DescOperacion,
      id_audiencia: audienciaActual?.id,
    };
    try {
      setCurrentOperation(`${MensOperacion} términos...`);
      setOperationLoading(true);
      await ProcesosApi.operarAudiencia(datosTerminos);
      setFechaNuevaAudiencia("");
      setHoraNuevaAudiencia("");
      setReloadFlag((prev) => !prev);
      if (onProcesoChange) {
        onProcesoChange();
      }
      await fetchDatosIniciales();
      alert(
        `La operación de ${MensOperacion} los Terminos del Proceso se ha realizado de manera satisfactoria.`
      );
    } catch (error) {
      console.error("Error en al Activar los Terminos del Proceso:", error);
      alert(obtenerMensajeError(error, "Ocurrió un error al Activar los Terminos del Proceso."));
    } finally {
      setOperationLoading(false);
      setCurrentOperation("");
    }
  };

  const handleCerrarProceso = async () => {
    if (isProcessing()) return;
    const etapas = audienciaActual?.etapas || [];
    const etapaQuorum = etapas.find((etapa) => etapa.orden === 1);
    if (etapaQuorum.estado !== "CUMPLIDA") {
      alert("No se puede avanzar: Quórum no completado.");
      return;
    }
    //const etapaResultado = etapas.find((etapa) => etapa.orden === 6);
    let mensajeConfirm = "";
    let OPERACION = "CERRAR_PROCESO";
    let FORMATO = "";
    if (etapaNegociacion.resultado?.resultado === "ACUERDO DE PAGO") {
      FORMATO = "cierreAcuerdo";
      mensajeConfirm = `El Proceso ${proceso.identificador} se cerrará en ACUERDO DE PAGO.`;
    } else if (etapaNegociacion.resultado?.resultado.includes("FRACASO")) {
      const tieneAcuerdoBilateral =
        etapaResultado.resultado?.info_acreedores &&
        Object.values(etapaResultado.resultado.info_acreedores).some(
          (a) => a.acuerdo_bilateral === true
        );
      if (tieneAcuerdoBilateral) {
        FORMATO = "cierreAcuerdoBilateral";
        mensajeConfirm = `El Proceso ${proceso.identificador} se cerrará en ${etapaNegociacion.resultado.resultado}, con Acuerdo Bilateral.`;
      } else {
        FORMATO = "cierreFracaso";
        mensajeConfirm = `El Proceso ${proceso.identificador} se cerrará en ${etapaNegociacion.resultado.resultado}.`;
      }
    } else {
      OPERACION = "TERMINAR_PROCESO";
      mensajeConfirm = `El Proceso ${proceso.identificador} se cerrará por Desistimiento sin tener ningun resultado.`;
    }
    const confirmacion = window.confirm(
      `¿Está seguro(a) de que desea cerrar el Proceso? ${mensajeConfirm}`
    );
    if (!confirmacion) return;
    const datosCierreProceso = {
      operacion: OPERACION,
      id_audiencia: audienciaActual?.id,
      resultado: etapaNegociacion.resultado.resultado,
    };
    try {
      setCurrentOperation("Cerrando proceso...");
      setOperationLoading(true);
      await ProcesosApi.operarAudiencia(datosCierreProceso);
      await generarPdfProceso(FORMATO);
      setFechaNuevaAudiencia("");
      setHoraNuevaAudiencia("");
      setReloadFlag((prev) => !prev);
      if (onProcesoChange) {
        onProcesoChange();
      }
      alert("El Proceso ha sido cerrada correctamente.");
    } catch (error) {
      console.error("Error en al Cerrar el Proceso:", error);
      alert(obtenerMensajeError(error, "Ocurrió un error al Cerrar el Proceso."));
    } finally {
      setOperationLoading(false);
      setCurrentOperation("");
    }
  };

  const formatearFechaHora = (fechaString, horaString) => {
    if (!fechaString) return "";
    // Extraer manualmente los componentes de la fecha
    const [year, month, day] = fechaString.split("-");
    const fechaFormateada = `${day}/${month}/${year}`;
    let horaFormateada = horaString;
    if (horaString) {
      const [hora, minutos] = horaString.split(":");
      // Crear una fecha ficticia solo para formatear la hora
      const fechaConHora = new Date();
      fechaConHora.setHours(parseInt(hora, 10), parseInt(minutos, 10), 0);
      horaFormateada = fechaConHora.toLocaleTimeString("es-CO", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }
    return `${fechaFormateada} a las ${horaFormateada}`;
  };

  const estadoStyle = {
    display: "inline-block",
    backgroundColor: audienciaActual?.estado === "SUSPENDIDA" ? "#E0E7FF" : "#24a147ff",
    color: audienciaActual?.estado === "SUSPENDIDA" ? "inherit" : "white",
    padding: "4px 8px",
    borderRadius: "12px",
    fontWeight: "bold",
    fontSize: "1.0rem",
    textAlign: "center",
    minWidth: "85px",
  };

  return (
    <Container>
      {isProcessing() && (
        <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <CircularProgress color="inherit" />
            <Typography variant="h6" sx={{ mt: 2, color: "white" }}>
              {getLoadingMessage()}
            </Typography>
          </Box>
        </Backdrop>
      )}
      {proceso ? (
        <>
          {loading ? (
            <Box display="flex" justifyContent="center" my={2}>
              <CircularProgress />
            </Box>
          ) : audiencias.length === 0 ? (
            <Typography variant="body1">No hay audiencias registradas.</Typography>
          ) : (
            <>
              <Tabs
                value={tabIndex}
                onChange={handleChangeTab}
                variant="scrollable"
                scrollButtons="auto"
              >
                {(audiencias || []).map((audiencia, index) => (
                  <Tab
                    key={audiencia.id}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <span>No. {audiencia.consecutivo}</span>
                        <IconButton
                          size="large"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            abrirModalEditarAudiencia(audiencia);
                          }}
                          disabled={
                            audiencia.estado === "SUSPENDIDA" ||
                            audiencia.estado === "CERRADA" ||
                            tabIndex !== index
                          }
                          sx={{
                            padding: "2px",
                            minWidth: "auto",
                            "&:hover": {
                              backgroundColor: "#E0E7FF",
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                    isActive={tabIndex === index}
                    sx={{
                      color: tabIndex === index ? "white !important" : "inherit",
                      backgroundColor: tabIndex === index ? "#1E40AF" : "#E0E7FF",
                      fontWeight: tabIndex === index ? "bold" : "normal",
                      borderRadius: "12px",
                      marginRight: "8px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: tabIndex === index ? "#1E40AF" : "#E0E7FF",
                      },
                    }}
                  />
                ))}
              </Tabs>
              <Box sx={{ p: 1, border: "1px solid #ddd", borderRadius: "10px", mt: 1 }}>
                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                  <Grid size={4} justifyContent="center" alignItems="center">
                    <SuiTypography textAlign="center">
                      <span style={estadoStyle}>{audienciaActual?.estado || ""}</span>
                    </SuiTypography>
                  </Grid>
                  <Grid size={4} justifyContent="center" alignItems="center">
                    <Typography variant="h6" fontWeight="bold" textAlign="left">
                      {formatearFechaHora(audienciaActual?.fecha, audienciaActual?.hora)}
                    </Typography>
                  </Grid>
                  <Grid
                    size={4}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {(audienciaActual?.estado === "SUSPENDIDA" ||
                      audienciaActual?.estado === "CERRADA") && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleActaAudiencia(
                            audienciaActual?.estado,
                            audienciaActual?.consecutivo
                          );
                        }}
                        sx={{
                          ml: 1,
                          fontSize: "0.75rem",
                          padding: "2px 8px",
                          borderRadius: "16px",
                          color: "#ffffff",
                          borderColor: "#ffffff",
                          backgroundColor: "#1976d2",
                          fontWeight: "500",
                          transition: "background-color 0.2s ease, color 0.2s ease",
                          "&:hover": {
                            backgroundColor: "#1565c0",
                            borderColor: "#ffffff",
                          },
                        }}
                      >
                        Ver Acta
                      </Button>
                    )}
                    {audienciaActual?.estado !== "SUSPENDIDA" &&
                      audienciaActual?.estado !== "CERRADA" &&
                      audienciaActual?.fecha &&
                      dayjs(audienciaActual.fecha).isAfter(dayjs(), "day") && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setModalConvocarOpen(true)}
                          sx={{
                            ml: 1,
                            fontSize: "0.75rem",
                            padding: "2px 8px",
                            borderRadius: "16px",
                            color: "#ffffff",
                            borderColor: "#ffffff",
                            backgroundColor: "#7c3aed",
                            fontWeight: "500",
                            transition: "background-color 0.2s ease, color 0.2s ease",
                            "&:hover": {
                              backgroundColor: "#6d28d9",
                              borderColor: "#ffffff",
                            },
                          }}
                        >
                          Convocar
                        </Button>
                      )}
                    {(audienciaActual?.estado === "SUSPENDIDA" ||
                      audienciaActual?.estado === "CERRADA") && (
                      <Button
                        variant="outlined"
                        size="small"
                        //onClick={handleAbrirAudiencia}
                        onClick={() => handleAbrirAudiencia(audienciaActual?.id)}
                        sx={{
                          ml: 1,
                          fontSize: "0.75rem",
                          padding: "2px 8px",
                          borderRadius: "16px",
                          color: "#ffffff",
                          borderColor: "#ffffff",
                          backgroundColor: "#388e3c", // verde
                          fontWeight: "500",
                          transition: "background-color 0.2s ease, color 0.2s ease",
                          "&:hover": {
                            backgroundColor: "#2e7d32",
                            borderColor: "#ffffff",
                          },
                        }}
                      >
                        Abrir Audiencia
                      </Button>
                    )}
                    {audienciaActual?.id === penultimaAudiencia?.id &&
                      penultimaAudiencia?.estado === "REALIZADA" && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleCerrarAudiencia}
                          sx={{
                            ml: 1,
                            fontSize: "0.75rem",
                            padding: "2px 8px",
                            borderRadius: "16px",
                            color: "#ffffff",
                            borderColor: "#ffffff",
                            backgroundColor: "#d32f2f",
                            fontWeight: "500",
                            transition: "background-color 0.2s ease, color 0.2s ease",
                            "&:hover": {
                              backgroundColor: "#b71c1c",
                              borderColor: "#ffffff",
                            },
                          }}
                        >
                          Cerrar Audiencia
                        </Button>
                      )}
                  </Grid>
                </Grid>
                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                  <Grid size={4} justifyContent="left" alignItems="left">
                    <Typography variant="h6" fontWeight="light" textAlign="left">
                      MODALIDAD: <strong>{audienciaActual?.modalidad || ""}</strong>
                    </Typography>
                  </Grid>
                  {audienciaActual?.modalidad === "Presencial" && (
                    <Grid size={8} justifyContent="left" alignItems="left">
                      <Typography variant="h6" fontWeight="light" textAlign="left">
                        LUGAR: <strong>{audienciaActual?.lugar || "Sin Lugar"}</strong>
                      </Typography>
                    </Grid>
                  )}
                  {audienciaActual?.modalidad === "Virtual" && (
                    <>
                      <Grid size={4} justifyContent="center" alignItems="center">
                        <Typography variant="h6" fontWeight="light" textAlign="left">
                          URL ZOOM: <strong>{audienciaActual?.enlace_zoom || "Sin Enlace"}</strong>
                        </Typography>
                      </Grid>
                      <Grid size={4} justifyContent="center" alignItems="center">
                        <Typography variant="h6" fontWeight="light" textAlign="left">
                          ID ZOOM: <strong>{audienciaActual?.id_zoom || "Sin ID Zoom"}</strong>
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                  <Grid size={12} justifyContent="center" alignItems="center">
                    <Typography variant="h6" fontWeight="light" textAlign="left">
                      OBSERVACIÓN: <strong>{audienciaActual?.observaciones || ""}</strong>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
              {(roles.isAdmin || roles.isOperador) && (
                <>
                  {audienciaActual?.estado !== "SUSPENDIDA" &&
                    audienciaActual?.estado !== "CERRADA" && (
                      <>
                        <Grid
                          container
                          columnSpacing={{ xs: 1, sm: 1, md: 1 }}
                          justifyContent="center"
                          alignItems="center"
                          sx={{ mt: 1 }}
                        >
                          <Grid size={3}>
                            <SuiTypography variant="h6" fontWeight="light">
                              Fecha y Hora Nueva Audiencia
                            </SuiTypography>
                          </Grid>
                          <Grid size={3}>
                            <SuiInput
                              fullWidth
                              type="date"
                              name="fechaNuevaAudiencia"
                              value={fechaNuevaAudiencia}
                              onChange={(e) => setFechaNuevaAudiencia(e.target.value)} // Guarda solo la fecha en formato YYYY-MM-DD
                              required
                            />
                          </Grid>
                          <Grid size={3}>
                            <SuiInput
                              fullWidth
                              type="time"
                              name="horaNuevaAudiencia"
                              value={horaNuevaAudiencia}
                              onChange={(e) => setHoraNuevaAudiencia(e.target.value)} // Guarda solo la hora en formato HH:mm
                              required
                            />
                          </Grid>
                          <Grid size={3}>
                            <SuiButton
                              variant="gradient"
                              buttonColor="info"
                              onClick={handleSuspenderAudiencia}
                              sx={{ marginRight: "4px" }}
                            >
                              Suspender
                            </SuiButton>
                          </Grid>
                        </Grid>
                        <Grid
                          container
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          columnSpacing={{ xs: 1, sm: 1, md: 1 }}
                          sx={{ mt: 1 }}
                        >
                          <SuiButton
                            variant="gradient"
                            buttonColor="white"
                            onClick={handleReprogramarAudiencia}
                            sx={{ marginRight: "4px" }}
                          >
                            Reprogramar
                          </SuiButton>
                          <SuiButton
                            variant="gradient"
                            buttonColor="error"
                            onClick={handleRevocarProceso}
                            sx={{ marginRight: "4px" }}
                          >
                            Revocar
                          </SuiButton>
                          <SuiButton
                            variant="gradient"
                            buttonColor="white"
                            onClick={handleProrrogarProceso}
                            sx={{ marginRight: "4px" }}
                          >
                            Prorrogar
                          </SuiButton>
                          <SuiButton
                            variant="gradient"
                            buttonColor={suspendido ? "success" : "info"}
                            onClick={handleSuspenderActivarTerminos}
                            sx={{ marginRight: "4px" }}
                          >
                            {suspendido ? "Activar Terminos" : "Suspender Terminos"}
                          </SuiButton>
                          <SuiButton
                            variant="gradient"
                            buttonColor="dark"
                            onClick={handleCerrarProceso}
                            sx={{ marginRight: "4px" }}
                          >
                            Cerrar
                          </SuiButton>
                          <SuiButton
                            variant="gradient"
                            buttonColor="error"
                            onClick={handleTerminarProceso}
                            sx={{ marginRight: "4px" }}
                          >
                            Terminar
                          </SuiButton>
                        </Grid>
                      </>
                    )}
                  {audienciaActual?.estado === "CERRADA" && (
                    <Grid
                      container
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      columnSpacing={{ xs: 1, sm: 1, md: 1 }}
                      sx={{ mt: 1 }}
                    >
                      <SuiButton
                        variant="gradient"
                        buttonColor="success"
                        onClick={handleAperturarProceso}
                        sx={{ marginRight: "4px" }}
                      >
                        Aperturar
                      </SuiButton>
                    </Grid>
                  )}
                </>
              )}
              <Box sx={{ border: "1px solid #ddd", borderRadius: "10px", mt: 1 }}>
                <Grid container sx={{ mb: 1 }}>
                  <Grid size={12} display="flex" justifyContent="center" alignItems="center">
                    <Tabs
                      value={tabIndexEtapas}
                      onChange={handleChangeEtapas}
                      variant="scrollable"
                      scrollButtons="auto"
                    >
                      {audienciaActual?.etapas?.map((etapa, index) => (
                        <Tab
                          key={index}
                          label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <span>{etapa.nombre}</span>
                              {etapa.estado === "CUMPLIDA" && (
                                <Box
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: "50%",
                                    backgroundColor: "#4CAF50",
                                    color: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "10px",
                                    fontWeight: "bold",
                                    ml: 0.5,
                                  }}
                                >
                                  ✓
                                </Box>
                              )}
                              {etapa.estado === "PENDIENTE" && (
                                <Box
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: "50%",
                                    backgroundColor: "#eab676",
                                    color: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "10px",
                                    fontWeight: "bold",
                                    ml: 0.5,
                                  }}
                                >
                                  P
                                </Box>
                              )}
                            </Box>
                          }
                          sx={{
                            color: tabIndexEtapas === index ? "white !important" : "inherit",
                            backgroundColor: tabIndexEtapas === index ? "#1E40AF" : "#E0E7FF",
                            fontWeight: tabIndexEtapas === index ? "bold" : "normal",
                            fontSize: "1.0rem",
                            padding: "4px 6px",
                            borderRadius: "10px",
                            marginRight: "4px",
                            transition: "background-color 0.3s ease, color 0.3s ease",
                            "&:hover": {
                              backgroundColor: tabIndexEtapas === index ? "#1E40AF" : "#E0E7FF",
                            },
                          }}
                        />
                      ))}
                    </Tabs>
                  </Grid>
                  {tabIndexEtapas === 0 && (
                    <ContainerQuorum
                      proceso={proceso}
                      audiencia={audienciaActual}
                      onAudienciaChange={handleAudienciaChange}
                    />
                  )}
                  {tabIndexEtapas === 1 && (
                    <ContainerReparos
                      proceso={proceso}
                      audiencia={audienciaActual}
                      onAudienciaChange={handleAudienciaChange}
                    />
                  )}
                  {tabIndexEtapas === 2 && (
                    <ContainerConciliacion
                      proceso={proceso}
                      audiencia={audienciaActual}
                      onAudienciaChange={handleAudienciaChange}
                    />
                  )}
                  {tabIndexEtapas === 3 && (
                    <ContainerObjeciones
                      proceso={proceso}
                      audiencia={audienciaActual}
                      onAudienciaChange={handleAudienciaChange}
                    />
                  )}
                  {tabIndexEtapas === 4 && (
                    <ContainerNegociacion
                      proceso={proceso}
                      audiencia={audienciaActual}
                      audienciaAnterior={penultimaAudiencia || null}
                      onAudienciaChange={handleAudienciaChange}
                    />
                  )}
                  {tabIndexEtapas === 5 && (
                    <ContainerResultado
                      proceso={proceso}
                      audiencia={audienciaActual}
                      onAudienciaChange={handleAudienciaChange}
                    />
                  )}
                </Grid>
              </Box>
              {/* Modal para visualizar PDF */}
              <FileDialog
                open={showPdfViewer}
                onClose={() => setShowPdfViewer(false)}
                title="Vista previa del PDF"
                content={
                  <embed
                    src={currentPdfUrl}
                    type="application/pdf"
                    width="100%"
                    height="500px"
                    style={{ border: "none" }}
                  />
                }
              />
            </>
          )}

          <EditarAudiencia
            open={modalEditar}
            onClose={() => {
              setModalEditar(false);
              setAudienciaParaEditar(null);
            }}
            audienciaId={audienciaParaEditar?.id}
            audiencia={audienciaParaEditar}
            onSuccess={() => {
              fetchDatosIniciales();
              setModalEditar(false);
              setAudienciaParaEditar(null);
              onProcesoChange();
            }}
          />
          <ReprogramaModal
            open={modalReprogramarOpen}
            onClose={() => {
              setModalReprogramarOpen(false);
            }}
            audienciaId={audienciaActual?.id}
            audiencia={audienciaActual}
            onSuccess={() => {
              fetchDatosIniciales();
              setModalReprogramarOpen(false);
              onProcesoChange();
            }}
          />
          <RevocacionModal
            open={modalRevocarOpen}
            onClose={() => {
              setModalRevocarOpen(false);
            }}
            audienciaId={audienciaActual?.id}
            audiencia={audienciaActual}
            onSuccess={() => {
              fetchDatosIniciales();
              setModalRevocarOpen(false);
              onProcesoChange();
            }}
          />
          <ProrrogaModal
            open={modalProrrogarOpen}
            onClose={() => {
              setModalProrrogarOpen(false);
            }}
            audienciaId={audienciaActual?.id}
            audiencia={audienciaActual}
            onSuccess={() => {
              fetchDatosIniciales();
              setModalProrrogarOpen(false);
              onProcesoChange();
            }}
          />
          <AperturaModal
            open={modalAperturarOpen}
            onClose={() => {
              setModalAperturarOpen(false);
            }}
            audienciaId={audienciaActual?.id}
            audiencia={audienciaActual}
            onSuccess={() => {
              fetchDatosIniciales();
              setModalAperturarOpen(false);
              onProcesoChange();
            }}
          />
          <TerminacionModal
            open={modalTerminarOpen}
            onClose={() => {
              setModalTerminarOpen(false);
            }}
            audienciaId={audienciaActual?.id}
            audiencia={audienciaActual}
            onSuccess={() => {
              fetchDatosIniciales();
              setModalTerminarOpen(false);
              onProcesoChange();
            }}
          />
          <ConvocarAudiencia
            open={modalConvocarOpen}
            onClose={() => setModalConvocarOpen(false)}
            audiencia={audienciaActual}
            proceso={proceso}
            onSuccess={() => {
              setModalConvocarOpen(false);
              fetchDatosIniciales();
            }}
          />
        </>
      ) : (
        <Typography>Cargando...</Typography>
      )}
    </Container>
  );
};

ContainerAudiencias.propTypes = {
  proceso: PropTypes.shape({
    id: PropTypes.number.isRequired,
    identificador: PropTypes.string.isRequired,
    prorrogas: PropTypes.shape({
      orden: PropTypes.number,
      cantidad_dias: PropTypes.number,
      fecha_creacion: PropTypes.Date,
      observacion: PropTypes.string,
      id_audiencia: PropTypes.number,
    }),
  }),
  onProcesoChange: PropTypes.func,
};

export default ContainerAudiencias;
